<template>
  <div
    class="v3dp__datepicker"
    :style="variables($attrs.style)">
    <popper
      :boundingEleID="parentEleId"
      :customAttribs="{ show: isFocused }"
      :disable="false"
      :hostEleID="`${uniqueIdLocal}-datepicker-display`"
      :popoverID="`${uniqueIdLocal}-datepicker-popover`"
      :tooltipMode="false"
      :warning="false"
      placement="bottom-start"
      @emitError="$emit('emitError', err)">
      <slot>
        <div
          :id="`${uniqueIdLocal}-datepicker-display`">
          <div
            :class="{
              disabled: disabled,
              'disabled-input': disabled,
            }">
            <div class="v3dp__input_wrapper">
              <span class="v3dp__calendar-icon">
                <font-awesome-icon :icon="faIcons.faCalendarDay" />
              </span>
              <input
                :id="`${uniqueIdLocal}-datepicker-input`"
                :class="{ 'invalid-date': !validDate}"
                :aria-invalid="!validDate"
                :aria-errormessage="!validDate ? 'Invalid date format' : ''"
                v-bind="$attrs"
                type="text"
                ref="inputRef"
                role="button"
                :aria-label="`Select ${label} Date`"
                :aria-readonly="!typeable"
                :disabled="disabled || null"
                :placeholder="placeholder"
                :readonly="!typeable"
                :tabindex="disabled ? -1 : 0"
                v-model="input"
                @keydown="keyDown"
                @blur="blur"
                @focus="focus"
              />
            </div>
          </div>
        </div>
      </slot>
      <template #popover>
        <div
          :id="`${uniqueIdLocal}-datepicker-popover`"
          class="v3dp__popover-z">
          <year-picker
            aria-modal
            v-show="viewShown === VIEW_YEAR"
            v-model:pageDate="pageDate"
            :cursor="cursor"
            :lowerLimit="lowerLimit"
            :parentEleId="parentEleId"
            :selected="modelValue"
            :upperLimit="upperLimit"
            @select="selectYear"
          />
          <month-picker
            aria-modal
            v-show="viewShown === VIEW_MONTH"
            v-model:pageDate="pageDate"
            :cursor="cursor"
            :parentEleId="parentEleId"
            :selected="modelValue"
            :format="monthListFormat"
            :headingFormat="dayPickerHeadingFormat"
            :locale="locale"
            :lowerLimit="lowerLimit"
            :upperLimit="upperLimit"
            @back="viewShown = VIEW_YEAR"
            @select="selectMonth"
          />
          <day-picker
            aria-modal
            v-show="viewShown === VIEW_DAY"
            v-model:pageDate="pageDate"
            :cursor="cursor"
            :disabledDates="disabledDates"
            :locale="locale"
            :lowerLimit="lowerLimit"
            :parentEleId="parentEleId"
            :selected="modelValue"
            :upperLimit="upperLimit"
            :weekStartsOn="weekStartsOn"
            :weekdayFormat="weekdayFormat"
            @back="viewShown = VIEW_MONTH"
            @select="selectDay"
          />
        </div>
      </template>
    </popper>
  </div>
</template>

<script>
import _ from 'lodash';

import {
  computed,
  defineComponent,
  ref,
  watch,
  watchEffect,
} from 'vue';
import { add, parse, isValid, format, max, min } from 'date-fns';

import Popper from 'components/Widgets/Popper/Popper';
import DayPicker from './DayPicker';
import MonthPicker from './MonthPicker';
import YearPicker from './YearPicker';

const VIEW_NONE = 'none';
const VIEW_DAY = 'day';
const VIEW_MONTH = 'month';
const VIEW_YEAR = 'year';
const TIME_RESOLUTIONS = [VIEW_DAY, VIEW_MONTH, VIEW_YEAR];

const boundedDate = (
  lower,
  upper,
  target = undefined,
) => {
  let date = target || new Date();

  if (lower) date = max([lower, date]);
  if (upper) date = min([upper, date]);

  return date;
};

export default defineComponent({
  components: {
    DayPicker,
    MonthPicker,
    Popper,
    YearPicker,
  },
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: '',
    },
    parentEleId: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    uniqueId: {
      type: String,
      required: false,
    },
    /**
     * `v-model` for selected date
     */
    modelValue: {
      type: Date,
      required: false,
    },
    /**
     * Dates not available for picking
     */
    disabledDates: {
      type: Object,
      required: false,
    },
    /**
     * Time not available for picking
     */
    disabledTime: {
      type: Object,
      required: false,
    },
    /**
     * Upper limit for available dates for picking
     */
    upperLimit: {
      type: Date,
      required: false,
    },
    /**
     * Lower limit for available dates for picking
     */
    lowerLimit: {
      type: Date,
      required: false,
    },
    /**
     * View on which the date picker should open. Can be either `year`,
     * `month`, `day` or `time`
     */
    startingView: {
      type: String,
      required: false,
      default: VIEW_DAY,
      validate: (v) =>
        typeof v === 'string' && TIME_RESOLUTIONS.includes(v),
    },
    /**
     * `date-fns`-type formatting for a month view heading
     */
    dayPickerHeadingFormat: {
      type: String,
      required: false,
      default: 'LLLL yyyy',
    },
    /**
     * `date-fns`-type formatting for the month picker view
     */
    monthListFormat: {
      type: String,
      required: false,
      default: 'LLL',
    },
    /**
     * `date-fns`-type formatting for a line of weekdays on day view
     */
    weekdayFormat: {
      type: String,
      required: false,
      default: 'EEEE',
    },
    /**
     * `date-fns`-type format in which the string in the input should be both
     * parsed and displayed
     */
    inputFormat: {
      type: String,
      required: false,
      default: 'yyyy-MM-dd',
    },
    /**
     * [`date-fns` locale object](https://date-fns.org/v2.16.1/docs/I18n#usage).
     * Used in string formatting (see default `dayPickerHeadingFormat`)
     */
    locale: {
      type: Object,
      required: false,
    },
    /**
     * Day on which the week should start.
     *
     * Number from 0 to 6, where 0 is Sunday and 6 is Saturday.
     * Week starts with a Monday (1) by default
     */
    weekStartsOn: {
      type: Number,
      required: false,
      default: 1,
      validator: (value) => [0, 1, 2, 3, 4, 5, 6].includes(value),
    },
    /**
     * Disables datepicker and prevents it's opening
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /*
     * Allows user to input date manually
     */
    typeable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * If set, lower-level views won't show
     */
    minimumView: {
      type: String,
      required: false,
      default: VIEW_DAY,
      validate: (v) =>
        typeof v === 'string' && TIME_RESOLUTIONS.includes(v),
    },
  },
  emits: {
    emitError: null,
    'update:modelValue': (value) =>
      value === null || value === undefined || isValid(value),
    opened: null,
    closed: null,
  },
  setup(props, { attrs, emit }) {
    const cursor = ref(null);
    const viewShown = ref(VIEW_NONE);
    const pageDate = ref(new Date());
    const inputRef = ref(null);
    const isFocused = ref(false);
    const validDate = ref(true);

    const name = 'date-picker';
    const uniqueIdLocal = ref(name);
    if (props.uniqueId) {
      // eslint-disable-next-line
      uniqueIdLocal.value = props.uniqueId;
    }

    // eslint-disable-next-line
    let lastSelectedDate = ref(props.modelValue);

    const input = ref('');
    watchEffect(() => {
      const parsed = parse(input.value, props.inputFormat, new Date());
      validDate.value = false;
      if (isValid(parsed)) {
        pageDate.value = parsed;
        validDate.value = true;
      }
    });

    watchEffect(
      () => {
        input.value =
          props.modelValue && isValid(props.modelValue)
            ? format(props.modelValue, props.inputFormat, {
              locale: props.locale,
            })
            : '';
      });

    const renderView = (view = VIEW_NONE) => {
      if (!props.disabled) {
        if (view === VIEW_NONE && viewShown.value !== VIEW_NONE) {
          cursor.value = null;
        }
        if (view !== VIEW_NONE && viewShown.value === VIEW_NONE) {
          if (cursor.value === null) {
            cursor.value = props.modelValue;
          }
          pageDate.value =
            props.modelValue || boundedDate(props.lowerLimit, props.upperLimit);
        }
        viewShown.value = view;

        if (view !== VIEW_NONE) {
          emit('opened');
        } else {
          emit('closed');
        }
      }
    };

    watchEffect(() => {
      if (props.disabled) viewShown.value = VIEW_NONE;
    });
    const selectYear = (date) => {
      pageDate.value = date;

      if (props.minimumView === VIEW_YEAR) {
        renderView(VIEW_NONE);
        emit('update:modelValue', date);
        lastSelectedDate.value = date;
      } else {
        viewShown.value = 'month';
      }
    };
    const selectMonth = (date) => {
      pageDate.value = date;

      if (props.minimumView === VIEW_MONTH) {
        renderView(VIEW_NONE);
        emit('update:modelValue', date);
        lastSelectedDate.value = date;
      } else {
        viewShown.value = VIEW_DAY;
      }
    };
    const selectDay = (date) => {
      pageDate.value = date;

      if (props.minimumView === VIEW_DAY) {
        renderView(VIEW_NONE);
        emit('update:modelValue', date);
        lastSelectedDate.value = date;
      } else {
        viewShown.value = 'time';
      }
    };

    const selectTime = (date) => {
      renderView(VIEW_NONE);
      emit('update:modelValue', date);
      lastSelectedDate.value = date;
    };

    const parseTypedDate = () => {
      const parsedDate = parse(
        inputRef?.value?.value,
        props.inputFormat,
        new Date(),
        { locale: props.locale },
      );
      validDate.value = false;
      if (
        isValid(parsedDate) &&
        input.value.length === props.inputFormat.length
      ) {
        input.value = inputRef.value?.value;
        emit('update:modelValue', parsedDate);
        lastSelectedDate.value = parsedDate;
        cursor.value = parsedDate;
        validDate.value = true;
      }
    };

    const typeableInputSameAsStartingDate = computed(() =>
      _.isDate(lastSelectedDate.value) &&
      lastSelectedDate.value.toISOString().slice(0, 10)
        === input.value,
    );

    const blur = () => {
      isFocused.value = false;
      renderView();
      if (props.typeable && !typeableInputSameAsStartingDate.value) {
        parseTypedDate();
      }
    };

    const keyDown = (event) => {
      let disabledDateFn = _.get(props, 'disabledDates.predicate');
      if (!_.isFunction(disabledDateFn)) disabledDateFn = (date) => false;

      const ESC = 'Escape';
      const ENTER = 'Enter';
      const LEFT = 'ArrowLeft';
      const RIGHT = 'ArrowRight';
      const UP = 'ArrowUp';
      const DOWN = 'ArrowDown';

      const adjustDateForDateCursor = (days) => {
        const date = add(cursor.value, { days });
        if (date.getMonth() === cursor.value.getMonth() &&
          !disabledDateFn(date)) {
          cursor.value = date;
        }
        // Switch to the previous or next month.
        if (date.getMonth() !== cursor.value.getMonth() &&
          !disabledDateFn(date)) {
          cursor.value = date;
          pageDate.value = date;
        }
      };

      const adjustMonthForMonthCursor = (months) => {
        const date = add(cursor.value, { months });
        if (date.getYear() === cursor.value.getYear() &&
          !disabledDateFn(date)) {
          cursor.value = date;
        }
      };

      const adjustYearForYearCursor = (years) => {
        const date = add(cursor.value, { years });
        if (Math.floor(date.getUTCFullYear() / 10) * 10 ===
          Math.floor(cursor.value.getUTCFullYear() / 10) * 10 &&
          !disabledDateFn(date)) {
          cursor.value = date;
        }
      };

      const cursorSelectsDifferentDate = !_.isNil(cursor.value) &&
        cursor.value.toISOString().slice(0, 10) !==
          props.modelValue.toISOString().slice(0, 10);

      const code = event.key ? event.key : event.which;
      // close calendar if escape or enter are pressed
      switch (code) {
        case ESC:
          if (inputRef.value) inputRef.value.blur();
          break;
        case ENTER: {
          if (cursorSelectsDifferentDate
            && typeableInputSameAsStartingDate.value) {
            if (viewShown.value === VIEW_DAY) selectDay(cursor.value);
            if (viewShown.value === VIEW_MONTH) selectMonth(cursor.value);
            if (viewShown.value === VIEW_YEAR) selectYear(cursor.value);
          } else if (inputRef.value) {
            inputRef.value.blur();
          }
          break;
        }
        case LEFT: {
          if (viewShown.value === VIEW_DAY) adjustDateForDateCursor(-1);
          if (viewShown.value === VIEW_MONTH) adjustMonthForMonthCursor(-1);
          if (viewShown.value === VIEW_YEAR) adjustYearForYearCursor(-1);
          break;
        }
        case RIGHT: {
          if (viewShown.value === VIEW_DAY) adjustDateForDateCursor(1);
          if (viewShown.value === VIEW_MONTH) adjustMonthForMonthCursor(1);
          if (viewShown.value === VIEW_YEAR) adjustYearForYearCursor(1);
          break;
        }
        case UP: {
          if (viewShown.value === VIEW_DAY) adjustDateForDateCursor(-7);
          if (viewShown.value === VIEW_MONTH) adjustMonthForMonthCursor(-3);
          if (viewShown.value === VIEW_YEAR) adjustYearForYearCursor(-3);
          break;
        }
        case DOWN: {
          if (viewShown.value === VIEW_DAY) adjustDateForDateCursor(7);
          if (viewShown.value === VIEW_MONTH) adjustMonthForMonthCursor(3);
          if (viewShown.value === VIEW_YEAR) adjustYearForYearCursor(3);
          break;
        }
        default:
          break;
      }

      if (props.typeable && !typeableInputSameAsStartingDate.value) {
        parseTypedDate();
      }
    };

    const initialView = computed(() => {
      const startingViewOrder = TIME_RESOLUTIONS.indexOf(props.startingView);
      const minimumViewOrder = TIME_RESOLUTIONS.indexOf(props.minimumView);

      return startingViewOrder < minimumViewOrder
        ? props.minimumView
        : props.startingView;
    });

    watch(() => props.modelValue, (newVal) => {
      lastSelectedDate.value = newVal;
      validDate.value = true;
    });

    const focus = () => {
      isFocused.value = true;
      renderView(initialView.value);
    };
    const FocusOnInputElement = async () => {
      inputRef.value.focus();
      isFocused.value = true;
      renderView(initialView.value);
    };

    const variables = (object) =>
      Object.fromEntries(
        Object.entries(object ?? {}).filter(([key, val]) =>
          key.startsWith('--')),
      );

    return {
      name,

      VIEW_DAY,
      VIEW_MONTH,
      VIEW_YEAR,

      // Parent accessible
      FocusOnInputElement,

      // Internal
      blur,
      cursor,
      focus,
      initialView,
      input,
      inputRef,
      isFocused,
      keyDown,
      log: (e) => console.log(e),
      pageDate,
      renderView,
      selectDay,
      selectMonth,
      selectTime,
      selectYear,
      uniqueIdLocal,
      validDate,
      variables,
      viewShown,
    };
  },
});
</script>

<style lang="scss">
  @import "~assets/css/colors";

  .v3dp__datepicker {
    --popout-bg-color: var(--vdp-bg-color, #fff);
    --box-shadow: var(
      --vdp-box-shadow,
      0 4px 10px 0 rgba(128, 144, 160, 0.1),
      0 0 1px 0 rgba(128, 144, 160, 0.81)
    );
    --text-color: var(--vdp-text-color, #000000);
    --border-radius: var(--vdp-border-radius, 3px);
    --heading-size: var(--vdp-heading-size, 2.5em); /* 40px for 16px font */
    --heading-weight: var(--vdp-heading-weight, bold);
    --heading-hover-color: var(--vdp-heading-hover-color, #eeeeee);
    --arrow-color: var(--vdp-arrow-color, currentColor);

    --elem-color: var(--vdp-elem-color, currentColor);
    --elem-lt-grey-color: var(--vdp-lt-grey-color, #ccc);
    --elem-disabled-color: var(--vdp-disabled-color, #444);
    --elem-disabled-bg-color: var(--vdp-disabled-bg-color, #999);
    --elem-hover-color: var(--vdp-hover-color, #fff);
    --elem-hover-bg-color: var(--secondary-color, $secondarycolor);
    --elem-cursor-color: var(--vdp-hover-color, #fff);
    --elem-cursor-bg-color: var(--secondary-color, $secondarycolor);
    --elem-selected-color: var(--vdp-selected-color, #fff);
    --elem-selected-bg-color: var(--primary-color, $primarycolor);

    --elem-current-outline-color: var(--vdp-current-date-outline-color, #888);
    --elem-current-font-weight: var(--vdp-current-date-font-weight, bold);

    --elem-font-size: var(--vdp-elem-font-size, 0.8em);
    --elem-border-radius: var(--vdp-elem-border-radius, 3px);

    --divider-color: var(--vdp-divider-color, var(--elem-disabled-color));

    position: relative;
  }

  .v3dp__calendar-icon {
    color: #c0c4cc;
    position: absolute;
    top: 6px;
    left: -1px;
  }

  .v3dp__popover-z {
    position: absolute;
    z-index: 500;
    left: -10px !important;
  }

  .invalid-date {
    color: $dangercolor;
  }
</style>
